import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import {
  UntypedFormGroup,
  UntypedFormControl,
  Validators,
  FormControl,
  FormArray,
  FormBuilder,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { NgDialogAnimationService } from 'src/app/services/core/ng-dialog-animation.service';
import { DialogService } from 'src/app/services/core/dialog.service';
import { ManageCategoriesDialogComponent } from '../manage-categories-dialog/manage-categories-dialog.component';
import { eventReminderOptions, eventDayOptions } from '../data';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { AddEditCalendarCategoryDialogComponent } from '../add-edit-calendar-category-dialog/add-edit-calendar-category-dialog.component';
import { ChangeEventArgs } from '@syncfusion/ej2-angular-calendars';
import { BackendApiService } from 'src/app/services/backend-api.service';
import { MatMenuTrigger } from '@angular/material/menu';
import { MAT_DATE_FORMATS, MatDateFormats } from '@angular/material/core';
import { SettingDataService } from 'src/app/modules/setting/setting.data.service';
import { first } from 'rxjs/operators';
import { EventCalendarDataService } from '../../../event-calendar.data.service';
import { ToasterService } from 'src/app/services/core/toaster.service';
import { finalize, forkJoin } from 'rxjs';
import { AppConstants } from 'src/app/constants/app.constants';

// Force 12-hour (AM/PM) time display on the Material timepicker so the event
// dialog matches the bid creation form. Date formats stay as the native defaults.
const EVENT_DIALOG_DATE_FORMATS: MatDateFormats = {
  parse: { dateInput: null, timeInput: null },
  display: {
    dateInput: { year: 'numeric', month: 'numeric', day: 'numeric' },
    monthYearLabel: { year: 'numeric', month: 'short' },
    dateA11yLabel: { year: 'numeric', month: 'long', day: 'numeric' },
    monthYearA11yLabel: { year: 'numeric', month: 'long' },
    timeInput: { hour: 'numeric', minute: '2-digit', hour12: true },
    timeOptionLabel: { hour: 'numeric', minute: '2-digit', hour12: true },
  },
};

@Component({
    selector: 'app-event-dialog',
    templateUrl: './event-dialog.component.html',
    styleUrls: ['./event-dialog.component.scss'],
    standalone: false,
    providers: [{ provide: MAT_DATE_FORMATS, useValue: EVENT_DIALOG_DATE_FORMATS }]
})
export class EventDialogComponent implements OnInit {
  // Detail-page route (relative to root) for each module an event can be
  // linked to, keyed by the backend's `event_reference_type` (lowercase
  // class basename of the event's polymorphic `reference_type`). Every
  // type shares the same brand accent color (#FF5733, the Bids
  // system-calendar / sidebar orange) — only the icon changes per type.
  private static readonly REFERENCE_COLOR = '#FF5733';

  private static readonly REFERENCE_ROUTES: Record<
    string,
    { path: string; label: string; icon: string }
  > = {
    bid: { path: '/bids/bid', label: 'Bid', icon: 'fa-gavel' },
    job: { path: '/jobs/detail', label: 'Job', icon: 'fa-briefcase' },
    task: { path: '/tasks/task', label: 'Task', icon: 'fa-clipboard-check' },
    ticket: { path: '/tickets/ticket', label: 'Ticket', icon: 'fa-ticket-alt' },
    purchaseorder: { path: '/purchase-order/detail', label: 'Purchase Order', icon: 'fa-file-invoice-dollar' },
  };

  // Import data from data.ts file
  public moduleId: any = '';
  public referenceId: any = '';
  reminderOptions = eventReminderOptions;
  reminderOption = eventReminderOptions[3];
  dayOptions = eventDayOptions;
  selectedDayOption = eventDayOptions[0];
  moduleCalendarId: any = '';

  dropdownSelectedCategories: any[] = [];
  isPrivate = false;
  selectedReminderOption: string = '';

  // Section expansion state
  expandedSections: Record<string, boolean> = {
    locationAttendees: true,
    description: true,
    reminder: false,
    attachments: true,
  };


  // Calendar groups and calendars for two-step selector
  calendarGroups: any[] = [];
  calendarsInGroup: any[] = [];
  selectedGroup: any = null;
  calendars: any[] = [];

  // Delete state
  confirmDelete = false;

  public minEndTime: Date | null = null;
  public isEndTimeEnabled: boolean = false;
  isTimeZoneToggled = false;
  inviteAttendees: any[] = [];
  locationList: any[] = [];
  starttimeZones: any = [];
  endtimeZones: any = [];
  selectedLocation: string = '';
  selectedAttendeesFromDropdown: any[] = [];
  selectedLocationsFromDropdown: any[] = [];
  showEndDatePicker: boolean = false;
  calendarId: any = '';
  categoriesByCalendarId: any[] = [];
  file_id: any;
  attachmentsArr = [];
  attachmentsToAdd: any[] = [];
  selectedStartTimeZone: any = null;
  cellData: any = null;
  editEventData: any = null;
  allowedFileTypes: any = [];
  @ViewChild('inviteAttendeesMenuTrigger')
  inviteAttendeesMenuTrigger!: MatMenuTrigger;
  @ViewChild('locationMenuTrigger') locationMenuTrigger!: MatMenuTrigger;

  public eventForm!: UntypedFormGroup;
  public Editor = ClassicEditor;
  ckeConfig = {
    toolbar: {
      items: [
        'undo',
        'redo',
        '|',
        'heading',
        '|',
        'bold',
        'italic',
        'link',
        '|',
        'strikethrough',
        '|',
        'bulletedList',
        'numberedList',
        '|',
        'blockQuote',
        'insertTable',
        '|',
        'fontSize',
        'fontColor',
        'fontBackgroundColor',
      ],
      // CHANGE: Move toolbar to bottom
      shouldNotGroupWhenFull: true,
    },
    placeholder: 'Add a description',
    language: 'en',
    table: {
      contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells'],
    },
    licenseKey: '',
  };

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialogRef: MatDialogRef<EventDialogComponent>,
    private dialogService: DialogService,
    public dialog: NgDialogAnimationService,
    public backendApiService: BackendApiService,
    public SettingDataService: SettingDataService,
    public eventCalendarDataService: EventCalendarDataService,
    public toasterService: ToasterService,
    private fb: FormBuilder,
    private router: Router
  ) {
    this.cellData = this?.data?.cellData;
    this.editEventData = this?.data?.editEvent;
    this.moduleId = this?.data?.moduleId;
    this.referenceId = this?.data?.bidId;
    this.moduleCalendarId = this?.data?.moduleCalendarId;
    this.allowedFileTypes = AppConstants.documentFileTypes;
    this.calendarGroups = (this?.data?.calendars || []).filter(
      (group: any) => group.is_system_group !== 1
    );
    const requestedId = this?.data?.selectedCalendarId;
    // Try to find which group contains the requested calendar
    let preselectedGroup: any = null;
    if (requestedId) {
      for (const group of this.calendarGroups) {
        const filtered = (group.calendars || []).filter(
          (cal: any) => cal.slug !== 'bids' && cal.slug !== 'jobs'
        );
        if (filtered.find((cal: any) => cal.id === requestedId)) {
          preselectedGroup = group;
          this.calendarsInGroup = filtered;
          this.calendarId = requestedId;
          break;
        }
      }
    }
    if (!preselectedGroup && this.calendarGroups.length > 0) {
      preselectedGroup = this.calendarGroups[0];
      this.calendarsInGroup = (preselectedGroup.calendars || []).filter(
        (cal: any) => cal.slug !== 'bids' && cal.slug !== 'jobs'
      );
      this.calendarId = this.calendarsInGroup[0]?.id ?? null;
    }
    this.selectedGroup = preselectedGroup ?? null;
  }

  ngOnInit(): void {
    this.initializeForm();
    if (!this.editEventData) {
      this.setInitialEndTime();
    } else {
      // For editing, ensure end time is properly set and enable end time picker
      const endTime = this.eventForm.get('endTime')?.value;
      if (endTime) {
        this.minEndTime = endTime;
        this.isEndTimeEnabled = true;
      }
    }
    this.getLocalizationData();
    this.updateEndDate();
    this.getCategoriesByCalendarId();

    if (this.cellData) {
      this.setFormValuesFromCellData(this.cellData);
    }
    this.editToggleTimeZoneHandler();

    // Auto-expand sections that have content when editing
    if (this.editEventData) {
      if (this.sectionHasContent('locationAttendees')) {
        this.expandedSections['locationAttendees'] = true;
      }
      if (this.sectionHasContent('description')) {
        this.expandedSections['description'] = true;
      }
      if (this.sectionHasContent('reminder')) {
        this.expandedSections['reminder'] = true;
      }
      if (this.sectionHasContent('attachments')) {
        this.expandedSections['attachments'] = true;
      }
    }
  }

  initializeForm() {
    const selectedDayOption = this.calculateDaysCount();
    const isRestricted = this.isRestrictedCalendarType();

    this.eventForm = new UntypedFormGroup({
      title: new UntypedFormControl(this.editEventData?.title, [
        Validators.required,
      ]),
      attendees: new UntypedFormControl(''),
      locations: new UntypedFormControl(''),
      description: new UntypedFormControl(this.editEventData?.description),
      isAllDay: new UntypedFormControl({
        value: this.editEventData?.is_all_day === 1 || false,
        disabled: isRestricted,
      }),
      selectedDate: new UntypedFormControl({
        value: this.editEventData?.event_timezone?.start_date
          ? new Date(this.editEventData.event_timezone.start_date)
          : this.editEventData?.start_date
          ? new Date(this.editEventData.start_date)
          : new Date(),
        disabled: isRestricted,
      }),
      selectedEndDate: new UntypedFormControl({
        value: this.editEventData?.event_timezone?.end_date
          ? new Date(this.editEventData.event_timezone.end_date)
          : this.editEventData?.end_date
          ? new Date(this.editEventData.end_date)
          : new Date(),
        disabled: isRestricted,
      }),
      startTime: new UntypedFormControl({
        value:
          this.editEventData?.event_timezone?.start_date &&
          this.editEventData?.event_timezone?.start_time
            ? new Date(
                `${this.editEventData.event_timezone.start_date}T${this.editEventData.event_timezone.start_time}`
              )
            : this.editEventData?.start_date_time
            ? new Date(this.editEventData.start_date_time)
            : new Date(),
        disabled: isRestricted,
      }),
      endTime: new UntypedFormControl({
        value:
          this.editEventData?.event_timezone?.end_date &&
          this.editEventData?.event_timezone?.end_time
            ? new Date(
                `${this.editEventData.event_timezone.end_date}T${this.editEventData.event_timezone.end_time}`
              )
            : this.editEventData?.end_date_time
            ? new Date(this.editEventData.end_date_time)
            : new Date(),
        disabled: isRestricted,
      }),
      startTimeZone: new UntypedFormControl({
        value: null,
        disabled: isRestricted,
      }),
      endTimeZone: new UntypedFormControl({
        value: null,
        disabled: isRestricted,
      }),
      isAllselectedstartDate: new UntypedFormControl({
        value: this.editEventData?.is_all_start_date
          ? new Date(this.editEventData.is_all_start_date)
          : new Date(),
        disabled: isRestricted,
      }),
      isAllselectedEndDate: new UntypedFormControl({
        value: this.editEventData?.is_all_end_date
          ? new Date(this.editEventData.is_all_end_date)
          : new Date(),
        disabled: isRestricted,
      }),
      selectedDayOption: new UntypedFormControl({
        value: selectedDayOption,
        disabled: isRestricted,
      }),
      isUpdateBidDate: new UntypedFormControl(
        this.editEventData?.is_module_reference_update === 1 || false
      ),

      // New FormArray for dynamic additional slots
      additionalSlots: this.fb.array([]),
    });

    if (this.editEventData) {
      this.selectedAttendeesFromDropdown = this.editEventData.attendees || [];
      this.selectedLocationsFromDropdown = this.editEventData.locations || [];
      this.isPrivate = this.editEventData.is_private === 1 ? true : false;
      this.dropdownSelectedCategories =
        this.editEventData.calendar_categories || [];

      // Initialize reminder option from backend data
      if (this.editEventData.reminder_type) {
        this.selectedReminderOption = this.editEventData.reminder_type;
      }
    }
    if (
      this.editEventData?.attach_files &&
      this.editEventData?.attach_files.length > 0
    ) {
      // Transform the attach_files to match the expected format for the attachment component
      this.attachmentsArr = this.editEventData.attach_files.map(
        (file: any) => ({
          attach_file: file,
          attachment_id: file.id,
          file_name: file.file_name,
          original_name: file.original_name,
          url: file.url,
          file_type: file.file_type,
          thumbnail: file.thumbnail,
        })
      );

      // Set the attachmentsToAdd array with the attachment IDs
      this.attachmentsToAdd = this.attachmentsArr.map(
        (file: any) => file.attachment_id
      );
      // Ensure at least one row exists
    }

    if (
      this.editEventData?.additional_time_slots &&
      this.editEventData.additional_time_slots.length > 0
    ) {
      this.populateAdditionalSlots(this.editEventData.additional_time_slots);
    }
  }

  private populateAdditionalSlots(additionalTimeSlots: any[]): void {
    this.additionalSlots.clear();
    additionalTimeSlots.forEach((slot: any) => {
      const timezoneData = slot?.event_slot_timezone;
      const startDateTime = `${timezoneData?.start_date} ${timezoneData?.start_time}`;
      const endDateTime = `${timezoneData?.end_date} ${timezoneData?.end_time}`;

      // Create FormGroup for this slot (only dates and times, no timezone)
      const slotFormGroup = this.fb.group({
        startDate: new UntypedFormControl(new Date(timezoneData?.start_date)),
        startTime: new UntypedFormControl(new Date(startDateTime)),
        endDate: new UntypedFormControl(new Date(timezoneData?.end_date)),
        endTime: new UntypedFormControl(new Date(endDateTime)),
      });

      this.additionalSlots.push(slotFormGroup);
    });
  }

  getFormData() {
    const formValue = this.eventForm.getRawValue();

    const formatDateForAPI = (date: Date | null) => {
      if (!date) return null;
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    };

    const formatTime = (date: Date | null) => {
      if (!date) return null;
      return date.toTimeString().split(' ')[0]; // Returns HH:MM:SS
    };

    const additionalTimeSlots = this.additionalSlots.controls.map(
      (control: any) => {
        const slotValue = control.value;
        return {
          start_date: formatDateForAPI(slotValue.startDate),
          start_time: formatTime(slotValue.startTime),
          end_date: formatDateForAPI(slotValue.endDate),
          end_time: formatTime(slotValue.endTime),
        };
      }
    );
    console.log('this.isTimeZoneToggled', this.isTimeZoneToggled);
    const payload = {
      title: formValue?.title,
      calendar_category_ids: this.dropdownSelectedCategories.map(
        (cat) => cat.id
      ),
      reminder_date_time: null,
      reminder_type: this.selectedReminderOption === "Don't remind me"
        ? ''
        : (this.selectedReminderOption || null),
      attendees: this.selectedAttendeesFromDropdown.map((att) => ({
        type: att.type,
        id: att.id,
      })),
      locations: this.selectedLocationsFromDropdown.map((loc) => ({
        type: loc.type,
        id: loc.id,
      })),
      calendar_id: this.calendarId ? this.calendarId : this.moduleCalendarId,
      is_private: this.isPrivate ? 1 : 0,
      description: formValue?.description,
      attachment_ids: this.attachmentsToAdd,
      is_all_day: formValue?.isAllDay ? 1 : 0,
      start_date: formatDateForAPI(formValue.selectedDate),
      end_date: this.isTimeZoneToggled
        ? formatDateForAPI(formValue.selectedEndDate)
        : formatDateForAPI(formValue.selectedDate),
      start_time: formatTime(formValue.startTime),
      end_time: formatTime(formValue.endTime),
      module_id: this.moduleId, // Use this.moduleId
      reference_id: this.referenceId ? Number(this.referenceId) : null,
      // Only send a timezone when the picker actually holds one. This control
      // is populated asynchronously (getLocalizationData), so a save that
      // happens first would otherwise send `null` — and the API reads that as
      // "clear the stored zone", which also skips its base-timezone conversion
      // and shifts the event by the UTC offset. Omitting the key instead leaves
      // the stored value untouched: both fields are `nullable` server-side.
      ...(formValue.startTimeZone?.zone
        ? {
            start_time_zone: formValue.startTimeZone.zone,
            end_time_zone: formValue.startTimeZone.zone, // Always use start timezone value
          }
        : {}),
      days_count: formValue.selectedDayOption.value,
      is_all_start_date: formatDateForAPI(formValue.isAllselectedstartDate),
      is_all_end_date: formatDateForAPI(formValue.isAllselectedEndDate),
      is_module_reference_update: formValue.isUpdateBidDate ? 1 : 0,
      additional_time_slots: additionalTimeSlots,
    };
    return payload;
  }

  save() {
    this.touchFormControls();

    // For restricted calendars, we need to enable controls temporarily for validation
    if (this.isRestrictedCalendarType()) {
      this.eventForm.enable();
    }

    if (this.eventForm.invalid) {
      // Re-disable the restricted fields if validation fails
      if (this.isRestrictedCalendarType()) {
        this.disableRestrictedFields();
      }
      return;
    }
    const payload = this.getFormData();
    console.log('payload', payload);

    if (this.editEventData) {
      // Synthetic delivery events ("delivery-N") have no events table
      // row. Route to the dedicated reschedule endpoint and send only
      // the fields a delivery actually owns. Detect via the marker the
      // backend feed already stamps on these rows.
      const deliveryId = this.parseDeliveryEventId(this.editEventData);
      if (deliveryId !== null) {
        const deliveryPayload = {
          scheduled_date: payload.start_date,
          scheduled_time: payload.start_time,
          receiving_notes: payload.description ?? null,
        };
        this.eventCalendarDataService
          .rescheduleDelivery(deliveryId, deliveryPayload)
          .subscribe({
            next: (response: any) => {
              this.toasterService.showSuccessMessage(
                response?.message ?? 'Delivery updated.'
              );
              this.close(true);
            },
            error: (err: any) => {
              this.toasterService.showErrorMessage(
                this.buildRescheduleErrorMessage(err)
              );
            },
          });
        return;
      }

      this.eventCalendarDataService
        .updateEvent(this.editEventData.id, payload)
        .subscribe({
          next: (response: any) => {
            this.toasterService.showSuccessMessage(response?.message);
            this.close(true);
          },
        });
    } else {
      this.eventCalendarDataService.createEvent(payload).subscribe({
        next: (response: any) => {
          this.toasterService.showSuccessMessage(response?.message);
          this.close(true);
        },
      });
    }
  }

  /**
   * Returns the numeric delivery id when this event is a synthetic
   * delivery row, else null. Prefers the explicit `delivery_id` marker
   * from the backend feed; falls back to parsing "delivery-N" from
   * the event id for forward compatibility.
   */
  private parseDeliveryEventId(event: any): number | null {
    if (!event) return null;
    if (event.is_delivery && Number.isFinite(Number(event.delivery_id))) {
      return Number(event.delivery_id);
    }
    const raw = String(event.id ?? '');
    const match = raw.match(/^delivery-(\d+)$/);
    return match ? Number(match[1]) : null;
  }

  /**
   * Map the backend's reschedule error envelope into an operator-
   * friendly toast. The most common failure here is the status-gate
   * (delivery already arrived / receiving / done / cancelled); each
   * status gets its own sentence so the operator knows *why* the
   * action was blocked rather than seeing a transition diagnostic.
   * Validation and unknown errors fall back to the backend's message.
   */
  private buildRescheduleErrorMessage(err: any): string {
    // The reschedule endpoint uses the sentinel shape (`code` field)
    // so the global HTTP interceptor stays silent and we own the
    // toast. Older callers using `error_code` are still supported
    // for forward compatibility.
    const sentinelCode = err?.code ?? err?.error_code;
    if (sentinelCode === 'invalid_status_transition') {
      const current = String(err?.current_status ?? '').toLowerCase();
      switch (current) {
        case 'arrived':
          return 'This delivery has already arrived and can no longer be rescheduled.';
        case 'receiving':
          return 'This delivery is currently being received and can no longer be rescheduled.';
        case 'putaway':
          return 'This delivery is in putaway and can no longer be rescheduled.';
        case 'completed':
          return 'This delivery is already complete and can no longer be rescheduled.';
        case 'cancelled':
          return 'This delivery has been cancelled and can no longer be rescheduled.';
      }
    }

    // Laravel validation envelope — { errors: { field: [msg, ...] } }.
    const errors = err?.errors;
    if (errors && typeof errors === 'object') {
      const first = Object.values(errors)[0];
      if (Array.isArray(first) && first.length) {
        return String(first[0]);
      }
    }

    return err?.message ?? 'Unable to update this delivery.';
  }

  getCategoriesByCalendarId() {
    // Resolve a non-empty id; if neither field carries one (e.g. the
    // warehouse delivery calendar opens this dialog without a module
    // calendar binding) skip the call entirely. Hitting the endpoint
    // with an empty path segment can only 404.
    const id = this.calendarId || this.moduleCalendarId;
    if (!id) {
      this.categoriesByCalendarId = [];
      return;
    }
    this.eventCalendarDataService
      .getCategoriesByCalendarId(id)
      .subscribe((response: any) => {
        this.categoriesByCalendarId = response?.data;
      });
  }

  getLocalizationData() {
    forkJoin({
      timezoneList: this.SettingDataService.getLocalizationData(),
      localization: this.SettingDataService.getLocalization(),
    })
      .pipe(first())
      .subscribe(({ timezoneList, localization }) => {
        this.data = timezoneList.data;
        this.starttimeZones = timezoneList.data.timezone;
        this.endtimeZones = timezoneList.data.timezone;

        // Only set default timezone if not editing an existing event
        if (!this.editEventData) {
          const systemTimezoneId = localization?.data?.time_zone;
          const defaultZone = systemTimezoneId
            ? this.starttimeZones.find(
                (z: any) => String(z.id) === String(systemTimezoneId)
              )
            : null;
          this.eventForm.get('startTimeZone')?.setValue(defaultZone || null);
          this.eventForm.get('endTimeZone')?.setValue(defaultZone || null);
        } else {
          // For editing, find and set the timezone from editEventData
          if (this.editEventData.start_time_zone) {
            const startZone = this.starttimeZones.find(
              (z: any) => z.zone === this.editEventData.start_time_zone
            );
            this.eventForm.get('startTimeZone')?.setValue(startZone || null);
            // Ensure end timezone mirrors start timezone
            this.eventForm.get('endTimeZone')?.setValue(startZone || null);
          }

          if (this.editEventData.end_time_zone) {
            const endZone = this.endtimeZones.find(
              (z: any) => z.zone === this.editEventData.end_time_zone
            );
            // Only set if different from start timezone, otherwise keep synced
            if (
              endZone &&
              this.eventForm.get('startTimeZone')?.value?.zone !== endZone.zone
            ) {
              this.eventForm.get('endTimeZone')?.setValue(endZone || null);
            }
          }
        }
      });
  }

  getLocationEnter(event: any) {
    const value = (event.target as HTMLInputElement).value;
    if (value && value.trim().length > 0) {
      this.backendApiService
        .getApi(`events/search/locations?search=${value}`)
        .subscribe((response: any) => {
          this.locationList = response?.data;
          // if (this.locationList.length > 0) {
          setTimeout(() => {
            this.locationMenuTrigger.openMenu();
          });
          // }
        });
    }
  }

  onLocationSelected(item: any) {
    if (!this.selectedLocationsFromDropdown.some((loc) => loc.id === item.id)) {
      this.selectedLocationsFromDropdown.push(item);
    }
    this.locationMenuTrigger.closeMenu();
    this.eventForm.get('locations')?.setValue('');
  }

  removeLocation(location: any) {
    const index = this.selectedLocationsFromDropdown.indexOf(location);
    if (index >= 0) {
      this.selectedLocationsFromDropdown.splice(index, 1);
    }
  }

  onLocationSpanClick() {
    const inputValue = this.eventForm.get('locations')?.value?.trim();
    if (!inputValue) return;

    // Check for duplicates (by address or by name for custom)
    const alreadyExists = this.selectedLocationsFromDropdown.some(
      (loc) =>
        (loc.address &&
          loc.address.toLowerCase() === inputValue.toLowerCase()) ||
        (loc.name && loc.name.toLowerCase() === inputValue.toLowerCase())
    );
    if (!alreadyExists) {
      // Add as a custom location object (no id, but with address field)
      this.selectedLocationsFromDropdown.push({ address: inputValue });
    }
    this.locationMenuTrigger.closeMenu();
    // Clear the input in the form control
    this.eventForm.get('locations')?.setValue('');
  }

  getInviteAttendeesEnter(event: any) {
    const value = (event.target as HTMLInputElement).value;
    if (value && value.trim().length > 0) {
      this.backendApiService
        .getApi(`events/attendees/search?search=${value}`)
        .subscribe((response: any) => {
          this.inviteAttendees = response?.data;
          if (this.inviteAttendees.length > 0) {
            setTimeout(() => {
              this.inviteAttendeesMenuTrigger.openMenu();
            });
          }
        });
    }
  }

  onInviteAttendeesSelected(item: any) {
    if (!this.selectedAttendeesFromDropdown.some((att) => att.id === item.id)) {
      this.selectedAttendeesFromDropdown.push(item);
    }
    this.inviteAttendeesMenuTrigger.closeMenu();
    this.eventForm.get('attendees')?.setValue('');
  }

  removeAttendee(attendee: any) {
    const index = this.selectedAttendeesFromDropdown.indexOf(attendee);
    if (index >= 0) {
      this.selectedAttendeesFromDropdown.splice(index, 1);
    }
  }

  onAllDayToggle() {
    console.log('All Day toggled:', this.eventForm.get('isAllDay')?.value);
    if (this.eventForm.get('isAllDay')?.value) {
      this.eventForm
        .get('isAllselectedstartDate')
        ?.setValue(this.eventForm.get('selectedDate')?.value);
      this.eventForm
        .get('isAllselectedEndDate')
        ?.setValue(this.eventForm.get('selectedEndDate')?.value);
    } else {
      this.eventForm
        .get('selectedDate')
        ?.setValue(this.eventForm.get('isAllselectedstartDate')?.value);
      this.eventForm
        .get('selectedEndDate')
        ?.setValue(this.eventForm.get('isAllselectedEndDate')?.value);
    }
  }

  attachmentId(event: any) {
    const data = event?.attachment;
    console.log('data', data);
    if (Array.isArray(data)) {
      this.attachmentsArr = data as any;
      console.log('this.attachmentsArr', this.attachmentsArr);
      this.attachmentsToAdd = this.attachmentsArr.map(
        (file: any) => file.attachment_id
      ) as any;
    }
    this.eventForm.get('attachments')?.setValue(this.attachmentsToAdd);
  }

  openManageCategoriesDialog() {
    const dialogRef = this.dialog.open(ManageCategoriesDialogComponent, {
      width: '800px',
      maxWidth: '800px',
      maxHeight: '80vh',
      panelClass: 'manage-categories-dialog-container',
      data: {
        calendarId: this.calendarId ? this.calendarId : this.moduleCalendarId,
      },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.getCategoriesByCalendarId();
      }
    });
  }

  openCategoriesDialog() {
    const dialogRef = this.dialog.open(AddEditCalendarCategoryDialogComponent, {
      width: '650px',
      maxWidth: '650px',
      maxHeight: '80vh',
      panelClass: 'add-edit-calendar-category-dialog-container',
      data: {
        calendarId: this.calendarId ? this.calendarId : this.moduleCalendarId,
      },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.getCategoriesByCalendarId();
      }
    });
  }

  selectReminderOption(option: string) {
    this.selectedReminderOption = option;
  }

  toggleCategory(category: any) {
    const idx = this.dropdownSelectedCategories.findIndex(
      (cat) => cat.id === category.id
    );
    if (idx > -1) {
      this.dropdownSelectedCategories.splice(idx, 1); // Unselect
    } else {
      this.dropdownSelectedCategories.unshift(category); // Add to the beginning
    }
  }

  // Helper to check if a category is selected
  isCategorySelected(category: any): boolean {
    return this.dropdownSelectedCategories.some(
      (cat) => cat.id === category.id
    );
  }

  togglePrivate() {
    this.isPrivate = !this.isPrivate;
  }

  toggleTimeZone() {
    this.isTimeZoneToggled = !this.isTimeZoneToggled;
  }

  editToggleTimeZoneHandler() {
    if (this.editEventData?.start_date && this.editEventData?.end_date) {
      this.isTimeZoneToggled =
        this.editEventData.start_date !== this.editEventData.end_date;
    }
  }

  onStartTimeChange(args: any): void {
    // Read the chosen value from the form control (set by the picker's
    // ControlValueAccessor) so this works regardless of the event shape — the
    // Syncfusion and Material timepickers emit different payloads.
    const raw = this.eventForm.get('startTime')?.value ?? args?.value ?? args;
    const start = raw ? new Date(raw) : null;
    // Guard against empty / invalid values so we never wipe an existing end time.
    if (!start || isNaN(start.getTime())) {
      return;
    }

    // Calculate next 15-minute interval for the default end time.
    let minutes = start.getMinutes();
    let nextMinutes = Math.ceil((minutes + 1) / 15) * 15;
    if (nextMinutes === 60) {
      start.setHours(start.getHours() + 1);
      nextMinutes = 0;
    }
    const end = new Date(start);
    end.setMinutes(nextMinutes, 0, 0);

    this.eventForm.get('endTime')?.setValue(end);
    // Set minEndTime and enable end time picker
    this.minEndTime = end;
    this.isEndTimeEnabled = true;
  }
  /**
   * Calculates the duration string to be displayed in the End Time picker's list.
   * This method is called from the itemTemplate in the HTML.
   * @param endTimeValue The time value of the list item being rendered.
   * @returns A formatted string like "(0.5 hour)" or an empty string.
   */

  public calculateDuration(endTimeValue: Date): string {
    const startTime = this.eventForm.get('startTime')?.value;
    if (!startTime || !endTimeValue) {
      return '';
    }
    const diffInMs = endTimeValue.getTime() - new Date(startTime).getTime();
    const diffInHours = diffInMs / (1000 * 60 * 60);
    if (diffInHours <= 0) {
      return '';
    }
    if (diffInHours < 1) {
      return `(${diffInHours.toFixed(2)} hour)`;
    } else if (diffInHours === 1) {
      return `(1 hour)`;
    } else {
      return `(${diffInHours.toFixed(2)} hours)`;
    }
  }

  setInitialEndTime() {
    const startTime: Date = this.eventForm.get('startTime')?.value;
    if (startTime) {
      let minutes = startTime.getMinutes();
      let nextMinutes = Math.ceil((minutes + 1) / 15) * 15;
      let end = new Date(startTime);
      if (nextMinutes === 60) {
        end.setHours(end.getHours() + 1);
        nextMinutes = 0;
      }
      end.setMinutes(nextMinutes, 0, 0);
      this.eventForm.get('endTime')?.setValue(end);
      this.minEndTime = end;
      this.isEndTimeEnabled = true;
    }
  }

  updateEndDate() {
    const startDate = this.eventForm.get('isAllselectedstartDate')?.value;
    const selectedDayOption = this.eventForm.get('selectedDayOption')?.value;
    if (!startDate) return;
    if (selectedDayOption.label === 'custom') {
      this.showEndDatePicker = true;
      return;
    }
    if (selectedDayOption.value === 1) {
      this.showEndDatePicker = false;
      this.eventForm.get('isAllselectedEndDate')?.setValue(new Date(startDate));
    } else {
      this.showEndDatePicker = true;
      const daysToAdd = selectedDayOption.value - 1;
      const end = new Date(startDate);
      end.setDate(end.getDate() + daysToAdd);
      this.eventForm.get('isAllselectedEndDate')?.setValue(end);
    }
  }

  onStartDateChange(date: Date) {
    this.eventForm.get('isAllselectedstartDate')?.setValue(date);
    this.updateEndDate();
  }

  onEndDateChange(date: Date) {
    this.eventForm.get('isAllselectedEndDate')?.setValue(date);
    const startDate = this.eventForm.get('isAllselectedstartDate')?.value;
    if (
      startDate &&
      new Date(startDate).toDateString() === new Date(date).toDateString()
    ) {
      const oneDayOption = this.dayOptions.find((opt) => opt.value === 1);
      this.eventForm
        .get('selectedDayOption')
        ?.setValue(oneDayOption || { label: '1 day', value: 1 });
      this.showEndDatePicker = false;
      return;
    }
    const diffTime = new Date(date).getTime() - new Date(startDate).getTime();
    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24)) + 1;
    const match = this.dayOptions.find((opt) => opt.value === diffDays);
    this.eventForm
      .get('selectedDayOption')
      ?.setValue(match || { label: 'custom', value: diffDays });
    this.showEndDatePicker = true;
  }

  get isAllselectedEndDateControl(): FormControl {
    return this.eventForm.get('isAllselectedEndDate') as FormControl;
  }

  selectDayOption(option: any) {
    this.eventForm.get('selectedDayOption')?.setValue(option);
    this.updateEndDate();
  }

  calculateDaysCount(): any {
    let selectedDayOption = this.dayOptions[0]; // Default to 1 day

    if (this.editEventData) {
      const startDate = this.editEventData.is_all_start_date
        ? new Date(this.editEventData.is_all_start_date)
        : new Date();
      const endDate = this.editEventData.is_all_end_date
        ? new Date(this.editEventData.is_all_end_date)
        : new Date();

      // Calculate the difference in days
      const diffTime = endDate.getTime() - startDate.getTime();
      const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24)) + 1;

      // Find the matching day option
      const matchingOption = this.dayOptions.find(
        (option) => option.value === diffDays
      );
      if (matchingOption) {
        selectedDayOption = matchingOption;
      } else {
        // If no exact match, create a custom option
        selectedDayOption = { label: `${diffDays} days`, value: diffDays };
      }
    }

    return selectedDayOption; // Return the calculated value
  }

  private setFormValuesFromCellData(cellData: any): void {
    if (cellData.startTime && cellData.endTime) {
      // Set the start date and time
      this.eventForm.patchValue({
        selectedDate: new Date(cellData.startTime),
        startTime: new Date(cellData.startTime),
        isAllselectedstartDate: new Date(cellData.startTime),
      });

      // Set the end date and time
      this.eventForm.patchValue({
        selectedEndDate: new Date(cellData.endTime),
        endTime: new Date(cellData.endTime),
        isAllselectedEndDate: new Date(cellData.endTime),
      });

      // Set all-day flag if applicable
      if (cellData.isAllDay !== undefined) {
        this.eventForm.patchValue({
          isAllDay: cellData.isAllDay,
        });
      }

      // Update the end date based on the new start date
      this.updateEndDate();
    }
  }

  public isRestrictedCalendarType(): boolean {
    if (!this.editEventData || !this.editEventData.slug) {
      return false;
    }
    const restrictedSlugs = [
      'delivery',
      'installservice-calendar',
      'attendancehr-calendar',
    ];
    return restrictedSlugs.includes(this.editEventData.slug);
  }

  private disableRestrictedFields(): void {
    if (this.isRestrictedCalendarType()) {
      const restrictedFields = [
        'isAllDay',
        'selectedDate',
        'selectedEndDate',
        'startTime',
        'endTime',
        'startTimeZone',
        'endTimeZone',
        'isAllselectedstartDate',
        'isAllselectedEndDate',
        'selectedDayOption',
      ];

      restrictedFields.forEach((field) => {
        this.eventForm.get(field)?.disable();
      });
    }
  }

  get additionalSlots(): FormArray {
    return this.eventForm.get('additionalSlots') as FormArray;
  }

  private createAdditionalSlot(): UntypedFormGroup {
    const now = new Date();
    const end = new Date(now);
    end.setMinutes(now.getMinutes() + 15, 0, 0);
    return this.fb.group({
      startDate: new UntypedFormControl(new Date()),
      startTime: new UntypedFormControl(now),
      endDate: new UntypedFormControl(new Date()),
      endTime: new UntypedFormControl(end),
    });
  }

  addAdditionalSlot(): void {
    this.additionalSlots.push(this.createAdditionalSlot());
  }

  removeAdditionalSlot(index: number): void {
    if (index > -1 && index < this.additionalSlots.length) {
      this.additionalSlots.removeAt(index);
    }
  }

  // Enforce: endDate >= startDate and auto-sync endDate to startDate on change
  onAdditionalStartDateChange(index: number, date: Date): void {
    const grp = this.additionalSlots.at(index) as UntypedFormGroup;
    if (!grp) return;

    grp.get('startDate')?.setValue(date);

    const currentEnd = grp.get('endDate')?.value;
    if (!currentEnd || new Date(currentEnd) < new Date(date)) {
      grp.get('endDate')?.setValue(new Date(date));
    }
  }

  onAdditionalEndDateChange(index: number, date: Date): void {
    const grp = this.additionalSlots.at(index) as UntypedFormGroup;
    if (!grp) return;

    const start = grp.get('startDate')?.value;
    if (start && new Date(date) < new Date(start)) {
      grp.get('endDate')?.setValue(new Date(start));
      return;
    }
    grp.get('endDate')?.setValue(date);
  }

  toggleSection(section: string): void {
    this.expandedSections[section] = !this.expandedSections[section];
  }

  sectionHasContent(section: string): boolean {
    switch (section) {
      case 'locationAttendees':
        return (
          this.selectedLocationsFromDropdown.length > 0 ||
          this.selectedAttendeesFromDropdown.length > 0
        );
      case 'description':
        return !!this.eventForm?.get('description')?.value;
      case 'reminder':
        return !!this.selectedReminderOption;
      case 'attachments':
        return this.attachmentsArr.length > 0;
      default:
        return false;
    }
  }

  deleteEvent(): void {
    if (!this.confirmDelete) {
      this.confirmDelete = true;
      return;
    }
    this.eventCalendarDataService
      .deleteEvent(this.editEventData.id)
      .subscribe({
        next: (response: any) => {
          this.toasterService.showSuccessMessage(response?.message);
          this.dialogRef.close({ deleted: true });
        },
      });
  }

  /**
   * The linked record's detail-page route, when this event is tied to a
   * module we have a known detail page for (`event_reference_type` +
   * `reference_id`, set server-side on the event). Null for plain
   * unassociated events, or references we don't have a page for yet
   * (e.g. JobScope, Warehouse).
   */
  get referenceLink(): { path: string; label: string; number: string; icon: string; color: string } | null {
    const type = this.editEventData?.event_reference_type;
    const id = this.editEventData?.reference_id;
    if (!type || !id) {
      return null;
    }
    const route = EventDialogComponent.REFERENCE_ROUTES[type];
    if (!route) {
      return null;
    }
    // Prefer the linked record's own number (Bid/Job/Task/PO); fall back
    // to its id for types with no number column (e.g. Ticket). `label`
    // (e.g. "Bid") stays for the tooltip; the button itself shows only
    // the number, relying on the icon for which type it is.
    const number = this.editEventData?.reference_number || id;
    return {
      ...route,
      number: `${number}`,
      path: `${route.path}/${id}`,
      color: EventDialogComponent.REFERENCE_COLOR,
    };
  }

  goToReference(): void {
    const link = this.referenceLink;
    if (!link) {
      return;
    }
    this.close();
    this.router.navigateByUrl(link.path);
  }

  /** `color` tinted to `alpha` opacity, for the reference chip's background/border. */
  tint(color: string, alpha: number): string {
    const hex = color.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  duplicateEvent(): void {
    const payload = this.getFormData();
    this.eventCalendarDataService.createEvent(payload).subscribe({
      next: (response: any) => {
        this.toasterService.showSuccessMessage('Event duplicated successfully');
        this.close(true);
      },
    });
  }


  close = (data: any = null) => {
    this.dialogRef.close(data);
  };

  touchFormControls() {
    Object.keys(this.eventForm.controls).forEach((key) => {
      this.eventForm.get(key)?.markAsTouched();
    });
  }

  onGroupChange(group: any): void {
    this.calendarsInGroup = (group?.calendars || []).filter(
      (cal: any) => cal.slug !== 'bids' && cal.slug !== 'jobs'
    );
    this.calendarId = this.calendarsInGroup[0]?.id ?? null;
    this.getCategoriesByCalendarId();
  }

  // Add new method to handle timezone changes
  onStartTimeZoneChange(selectedZone: any): void {
    if (selectedZone) {
      // Automatically set end timezone to same as start timezone
      this.eventForm.get('endTimeZone')?.setValue(selectedZone);
    }
  }

  // Handle selectedDate change to ensure end date is not earlier than start date
  onSelectedDateChange(date: Date): void {
    if (date) {
      const selectedEndDate = this.eventForm.get('selectedEndDate')?.value;

      // If end date exists and is earlier than the new start date, update it
      if (selectedEndDate && new Date(selectedEndDate) < new Date(date)) {
        this.eventForm.get('selectedEndDate')?.setValue(new Date(date));
      }
    }
  }
}
